import {useEffect, useState} from 'react';
import Icon from '../../../../atoms/Icon/Icon';
import RangeSlider from '../../../../atoms/RangeSlider/RangeSlider';
import Switch from '../../../../atoms/Switch/Switch';
import Accordion from '../../../Accordion/Accordion';
import Textfield from '../../../Textfield/Textfield';
import StyledPrice, {
  StyledErrorMsg,
  StyledInputRow,
  StyledPriceBody,
  StyledPriceRange,
  StyledSecondaryHeader,
} from './styles';
import Props from './typings';

export const getPriceTitle = (currency, minVal, maxVal, onSale, freeShipping) => {
  const defaultTitle = 'Price';
  const titleArray: string[] = [];
  let selectedFilters = 0;
  if (minVal && maxVal) {
    titleArray.push(`${currency}${minVal} - ${currency}${maxVal}`);
    selectedFilters++;
  } else if (minVal) {
    titleArray.push(`> ${currency}${minVal}`);
    selectedFilters++;
  } else if (maxVal) {
    titleArray.push(`< ${currency}${maxVal}`);
    selectedFilters++;
  }

  if (onSale) selectedFilters++;
  if (freeShipping) selectedFilters++;
  if (titleArray.length === 0) {
    titleArray.push(defaultTitle);
  }

  return {label: titleArray, count: selectedFilters};
};

const Price = ({
  currency,
  priceBars,
  minVal,
  maxVal,
  collapsible = false,
  defaultCollapsed = false,
  onSale,
  freeShipping,
  priceRangeCallback,
  onSaleCallback,
  freeShippingCallback,
  errorCallback,
}: Props) => {
  const title = 'Price';
  const [inputFields, setInputFields] = useState({
    min: {
      value: minVal ? minVal.toString() : '',
      hasError: false,
    },
    max: {
      value: maxVal ? maxVal.toString() : '',
      hasError: false,
    },
  });
  const [errorList, setErrorList] = useState<string[]>([]);

  const capitalize = (string): string => string[0].toUpperCase() + string.slice(1);

  const isNumber = (value) => (isNaN(+value) ? false : true);

  const avgPrice = () => {
    const totalItems: number = priceBars.reduce((acc, bar) => bar.items_count + acc, 0);
    const totalPrice: number = priceBars.reduce(
      (accumulator, bar) =>
        bar.price_avg ? bar.price_avg * bar.items_count + accumulator : accumulator,
      0,
    );

    return Math.round(totalPrice / totalItems);
  };

  const subtitle = () => {
    return `The average artwork price based on your filters is ${currency}${avgPrice()}.`;
  };

  const errorCheck = (fields) => {
    const errors: string[] = [];
    let minHasError = false;
    let maxHasError = false;

    if (
      fields.min.value !== '' &&
      fields.max.value !== '' &&
      Number(fields.min.value) > Number(fields.max.value)
    ) {
      const errorMsg = 'Maximum price value must be bigger than minimum';
      errors.push(errorMsg);
      minHasError = true;
      maxHasError = true;
    }

    if (
      fields.min.value !== '' &&
      fields.max.value !== '' &&
      Math.abs(Number(fields.min.value) - Number(fields.max.value)) < 100
    ) {
      const errorMsg = 'The difference between minimum and maximum values should be at least 100';
      errors.push(errorMsg);
      minHasError = true;
      maxHasError = true;
    }

    for (const field in fields) {
      if (Number(fields[field].value) % 1 !== 0) {
        const errorMsg = 'Decimals are not supported';
        if (field === 'min') {
          minHasError = true;
        } else {
          maxHasError = true;
        }
        if (!errors.includes(errorMsg)) errors.push(errorMsg);
      }
    }

    setInputFields({
      min: {...fields.min, hasError: minHasError},
      max: {...fields.max, hasError: maxHasError},
    });
    setErrorList(errors);

    if (errors.length === 0) {
      priceRangeCallback({
        price_min: Number(fields.min.value) === 0 ? null : Number(fields.min.value),
        price_max:
          Number(fields.max.value) === 10000 || Number(fields.max.value) === 0
            ? null
            : Number(fields.max.value),
      });
    }
  };

  const onInputChange = (field, value) => {
    if (!isNumber(value)) return;

    const newData = {
      ...inputFields,
      [field]: {value: value, hasError: inputFields[field].hasError},
    };

    errorCheck(newData);
  };

  const onSliderChange = (obj) => {
    if (errorList.length > 0) setErrorList([]);

    setInputFields({
      min: {value: obj.min ? obj.min.toString() : '', hasError: false},
      max: {value: obj.max ? obj.max.toString() : '', hasError: false},
    });
  };

  const onSliderRelease = () => {
    priceRangeCallback({
      price_min: Number(inputFields.min.value) === 0 ? null : Number(inputFields.min.value),
      price_max:
        Number(inputFields.max.value) === 10000 || Number(inputFields.max.value) === 0
          ? null
          : Number(inputFields.max.value),
    });
  };

  useEffect(() => {
    if (errorCallback) errorCallback(errorList.length > 0);
  }, [errorList]);

  const fragmentBody = () => (
    <StyledPriceBody>
      <StyledPriceRange>
        <StyledSecondaryHeader>Select your range</StyledSecondaryHeader>

        <RangeSlider
          max={10000}
          step={100}
          minVal={
            inputFields.min.hasError === true
              ? undefined
              : Number(inputFields.min.value) === 0
              ? undefined
              : Number(inputFields.min.value)
          }
          maxVal={
            inputFields.max.hasError === true
              ? undefined
              : Number(inputFields.max.value) === 0
              ? undefined
              : Number(inputFields.max.value)
          }
          thumbGap={100}
          rangeBars={priceBars}
          prefix={currency}
          infinite
          onChange={onSliderChange}
          onMouseUp={onSliderRelease}
        />

        <StyledSecondaryHeader>Or input a range</StyledSecondaryHeader>

        <StyledInputRow>
          {Object.keys(inputFields).map((field, key) => (
            <Textfield
              placeholder={capitalize(field)}
              disabled={false}
              value={inputFields[field].value}
              error={inputFields[field].hasError}
              callback={(val) => onInputChange(field, val)}
              icon={inputFields[field].hasError ? 'alert' : undefined}
              key={key}
            />
          ))}
        </StyledInputRow>

        {errorList.length > 0 && (
          <StyledErrorMsg className="error-text">
            {errorList.map((error, key) => (
              <p key={key}>
                <Icon type="alert" /> {error}
              </p>
            ))}
          </StyledErrorMsg>
        )}
      </StyledPriceRange>

      <Switch
        title="On sale"
        subtitle="Show only artworks with up to 50% off."
        checked={onSale}
        callback={onSaleCallback}
      />

      <Switch
        title="Free shipping"
        subtitle="Show only artworks with free shipping."
        checked={freeShipping}
        callback={freeShippingCallback}
      />
    </StyledPriceBody>
  );

  const collapsibleFragment = () => (
    <Accordion title={title} subtitle={subtitle()} defaultCollapsed={defaultCollapsed}>
      {fragmentBody()}
    </Accordion>
  );

  const fragment = () => (
    <StyledPrice>
      <h4>{title}</h4>

      <p>{subtitle()}</p>

      {fragmentBody()}
    </StyledPrice>
  );

  return collapsible === true ? collapsibleFragment() : fragment();
};

export default Price;
